
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Logo } from './header/Logo';
import { DesktopNav } from './header/DesktopNav';
import { SearchBar } from './header/SearchBar';
import { MobileMenu } from './header/MobileMenu';
import { usePagePresence } from '@/hooks/usePagePresence';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';

interface MenuItem {
  id: string;
  title: string;
  url: string;
  is_external: boolean;
}

const Header = () => {
  usePagePresence();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { settings } = useWebsiteSettings();

  // Apply favicon when settings change
  useEffect(() => {
    if (settings?.favicon_url) {
      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
      existingFavicons.forEach(link => link.remove());

      // Add new favicon
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = settings.favicon_url;
      favicon.type = 'image/x-icon';
      document.head.appendChild(favicon);
    }
  }, [settings?.favicon_url]);

  // Fetch dynamic header menu items
  const { data: menuItems = [] } = useQuery({
    queryKey: ['header-menu-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('position', 'header')
        .eq('is_active', true)
        .order('order_index');
      if (error) throw error;
      return data || [];
    },
  });

  // Static fallback navigation items if no dynamic items exist
  const staticNavigation = [
    { name: 'Home', href: '/', isExternal: false },
    { name: 'Categories', href: '/categories', isExternal: false },
    { name: 'Trending', href: '/trending', isExternal: false },
    { name: 'New Games', href: '/new', isExternal: false },
  ];

  // Use dynamic menu items if available, otherwise fallback to static
  const navigation = menuItems.length > 0 
    ? menuItems.map((item: MenuItem) => ({
        name: item.title,
        href: item.url,
        isExternal: item.is_external
      }))
    : staticNavigation;

  return (
    <header className="sticky top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-lg">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-purple-50/20 to-pink-50/30"></div>
      
      <div className="relative w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-18">
          
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-1 justify-center max-w-2xl mx-8">
            <DesktopNav navigation={navigation} />
          </div>

          {/* Right Section: Search + Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <div className="hidden lg:block">
              <SearchBar />
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="relative text-gray-600 hover:text-blue-600 hover:bg-blue-50/50 rounded-xl transition-all duration-300 border border-transparent hover:border-blue-200/50 shadow-sm hover:shadow-md"
              >
                <div className="relative z-10">
                  {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden relative z-40">
            <MobileMenu navigation={navigation} onClose={() => setIsMenuOpen(false)} />
          </div>
        )}
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
    </header>
  );
};

export default Header;
